import React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import {
	optimizationGroupQueries,
	useUpdateOptimizationGroupMutation,
} from '@/entities/optimization-group'

import {
	FormProvider,
	RHFTextArea,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import { Loader } from '@/shared/components/ui/loader'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { ampli } from '@/shared/lib/ampli'

type FormData = {
	name: string
	description: string | null
}
const formDataSchema = z.object({
	name: z.string().min(2).max(500),
	description: z.string().max(500).nullable(),
})
function UpdateGeneralInfoModalContent({
	close,
	optimizationGroupId,
}: {
	optimizationGroupId: string
	close: () => void
}) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const updateOptimizationGroupMutation = useUpdateOptimizationGroupMutation(
		optimizationGroupId,
		{
			onSuccess() {
				queryClient.invalidateQueries({
					queryKey: ['optimization-groups'],
				})
				toast.success(
					t('toast.entity.updated', {
						entity: t('entity.optimization_group'),
					})
				)
				close()
			},
		}
	)
	const onSubmit = (data: FormData) => {
		ampli.ogSettingsDescriptionModalSubmitForm()
		updateOptimizationGroupMutation.mutate({
			...data,
			description: data.description || null,
		})
	}
	const methods = useForm<FormData>({
		resolver: zodResolver(formDataSchema),
		defaultValues: async () => {
			const optimizationGroup = await queryClient.fetchQuery(
				optimizationGroupQueries.single(optimizationGroupId)
			)

			return {
				name: optimizationGroup.name,
				description: optimizationGroup.description || '',
			}
		},
	})

	return (
		<>
			<ModalHeader>
				<ModalTitle>
					{t('app.edit_name', { defaultValue: 'Edit name' })}
				</ModalTitle>
			</ModalHeader>

			<FormProvider onSubmit={methods.handleSubmit(onSubmit)} methods={methods}>
				<ModalBody className='relative flex flex-col gap-3 overflow-hidden'>
					<Blanket
						isTinted={updateOptimizationGroupMutation.isPending}
						className='absolute flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-xs dark:bg-black/30'
					>
						<Loader />
					</Blanket>
					{updateOptimizationGroupMutation.error && (
						<Alert variant='destructive'>
							<AlertDescription>{t('fatal_error_title')}</AlertDescription>
						</Alert>
					)}
					<RHFTextField
						className='w-full'
						label={t('general_name')}
						placeholder={t('og.name.placeholder')}
						name='name'
					/>
					<RHFTextArea
						className='w-full'
						rows={7}
						label={t('general_description')}
						placeholder={t('og.description.placeholder')}
						name='description'
					/>
				</ModalBody>
			</FormProvider>
			<ModalFooter className='justify-end space-x-2'>
				<Button
					onClick={close}
					disabled={updateOptimizationGroupMutation.isPending}
				>
					{t('general_cancel')}
				</Button>
				<Button
					variant='primary'
					disabled={updateOptimizationGroupMutation.isPending}
					onClick={methods.handleSubmit(onSubmit)}
				>
					{t('general_save')}
				</Button>
			</ModalFooter>
		</>
	)
}

export default function UpdateGeneralInfoModal({
	isOpen,
	close,
	optimizationGroupId,
}: {
	isOpen: boolean
	close: () => void
	optimizationGroupId: string
}) {
	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent>
				<UpdateGeneralInfoModalContent
					optimizationGroupId={optimizationGroupId}
					close={close}
				/>
			</ModalContent>
		</Modal>
	)
}
